<template>
  <v-container fluid class="pa-0">
    <v-tabs v-model="tab" center-active show-arrows height="36" dense>
      <v-tab key="General">General</v-tab>
      <template v-for="calculation in calculations">
        <v-tab v-if="computedSettings[calculation.key].active" :key="`${calculation.key}`">{{
          calculation.name
        }}</v-tab>
      </template>
    </v-tabs>
    <v-tabs-items v-model="tab">
      <v-tab-item key="General">
        <v-card class="d-flex justify-center" flat>
          <v-card class="pa-2 ma-2" outlined width="700">
            <v-card-text>
              <v-card flat class="mb-6">
                <p class="text-subtitle-1 font-weight-medium">Model Timeline</p>
                <v-row class="align-baseline">
                  <v-col>
                    <v-text-field
                      v-model.number="computedSettings.year_start"
                      :label="schema.year_start.title"
                      :hint="schema.year_start.tooltip"
                      :error-messages="
                        $utils.getIfExists(errors, ['settings', 'year_start', 'messages'])
                      "
                      type="number"
                      @change="$emit('change')"
                      dense
                    >
                    </v-text-field>
                  </v-col>
                  <v-col>
                    <v-text-field
                      v-model.number="computedSettings.year_end"
                      :label="schema.year_end.title"
                      :hint="schema.year_end.tooltip"
                      :error-messages="
                        $utils.getIfExists(errors, ['settings', 'year_end', 'messages'])
                      "
                      type="number"
                      dense
                      @change="$emit('change')"
                    >
                    </v-text-field>
                  </v-col>
                  <v-col>
                    <p class="mb-0 text-subtitle-2">Total Years: {{ num_years }}</p>
                  </v-col>
                </v-row>
                <v-divider></v-divider>
                <v-list flat>
                  <v-subheader>Calculations</v-subheader>
                  <template>
                    <v-list-item v-for="calculation in calculations" :key="calculation.key">
                      <template #default>
                        <v-list-item-action>
                          <v-switch
                            v-model="computedSettings[calculation.key].active"
                            @change="$emit('change')"
                          ></v-switch>
                        </v-list-item-action>

                        <v-list-item-content>
                          <v-list-item-title>{{ calculation.name }}</v-list-item-title>
                        </v-list-item-content>
                      </template>
                    </v-list-item>
                  </template>
                </v-list>
              </v-card>
            </v-card-text>
          </v-card>
        </v-card>
      </v-tab-item>
      <v-tab-item v-if="computedSettings.ALTERNATIVE_ANALYSIS.active" key="Alternative Analysis">
        <v-card class="d-flex justify-center" flat>
          <v-card class="pa-2 ma-2" outlined width="700">
            <v-card-text>
              <v-switch
                label="Enable MIP"
                hint="Binary Variables"
                persistent-hint
                v-model="computedSettings.ALTERNATIVE_ANALYSIS.integer"
                :error-messages="
                  $utils.getIfExists(errors, [
                    'settings',
                    'STACKED_SERVICES_EMULATOR',
                    'integer',
                    'messages',
                  ])
                "
                :class="`my-${inputMargin}`"
                dense
                @change="$emit('change')"
              ></v-switch>
              <v-switch
                v-model="computedSettings.ALTERNATIVE_ANALYSIS.output_formulation"
                :label="schema.ALTERNATIVE_ANALYSIS.properties.output_formulation.title"
                :hint="schema.ALTERNATIVE_ANALYSIS.properties.output_formulation.tooltip"
                persistent-hint
                :error-messages="
                  $utils.getIfExists(errors, [
                    'settings',
                    'ALTERNATIVE_ANALYSIS',
                    'output_formulation',
                    'messages',
                  ])
                "
                :class="`my-${inputMargin}`"
                dense
                @change="$emit('change')"
              ></v-switch>
              <v-slider
                v-model.number="computedSettings.ALTERNATIVE_ANALYSIS.tolerance"
                :label="`Tolerance: 1e${settings.ALTERNATIVE_ANALYSIS.tolerance}`"
                :hint="schema.ALTERNATIVE_ANALYSIS.properties.tolerance.tooltip"
                :error-messages="
                  $utils.getIfExists(errors, [
                    'settings',
                    'ALTERNATIVE_ANALYSIS',
                    'tolerance',
                    'messages',
                  ])
                "
                max="-1"
                min="-38"
                always-dirty
                thumb-label="always"
                :class="`my-${inputMargin}`"
                dense
                @change="$emit('change')"
              ></v-slider>
              <InputTextField
                v-model.number="computedSettings.ALTERNATIVE_ANALYSIS.timeout"
                :label="schema.ALTERNATIVE_ANALYSIS.properties.timeout.title"
                :hint="schema.ALTERNATIVE_ANALYSIS.properties.timeout.tooltip"
                :error-messages="
                  $utils.getIfExists(errors, [
                    'settings',
                    'ALTERNATIVE_ANALYSIS',
                    'timeout',
                    'messages',
                  ])
                "
                suffix="seconds"
                type="number"
                :class="`my-${inputMargin}`"
                @change="$emit('change')"
              ></InputTextField>
              <InputGeneric
                :label="schema.ALTERNATIVE_ANALYSIS.properties.representative_days.title"
                :hint="schema.ALTERNATIVE_ANALYSIS.properties.representative_days.tooltip"
              >
                <v-select
                  v-model="computedSettings.ALTERNATIVE_ANALYSIS.representative_days"
                  persistent-hint
                  :error-messages="
                    $utils.getIfExists(errors, [
                      'settings',
                      'ALTERNATIVE_ANALYSIS',
                      'representative_days',
                      'messages',
                    ])
                  "
                  :items="['Clustering', 'Renewable Variability', 'User Defined']"
                  @change="$emit('change')"
                ></v-select>
              </InputGeneric>
              <InputDateArray
                v-if="settings.ALTERNATIVE_ANALYSIS.representative_days === 'User Defined'"
                v-model="computedSettings.ALTERNATIVE_ANALYSIS.chosen_days"
                :year="settings.year_start"
                :error-messages="
                  $utils.getIfExists(errors, [
                    'settings',
                    'ALTERNATIVE_ANALYSIS',
                    'chosen_days',
                    'messages',
                  ])
                "
                ordinal
                :class="`my-${inputMargin}`"
                @change="$emit('change')"
              >
              </InputDateArray>
              <v-slider
                v-if="settings.ALTERNATIVE_ANALYSIS.representative_days !== 'User Defined'"
                v-model.number="computedSettings.ALTERNATIVE_ANALYSIS.cluster_days"
                :error-messages="
                  $utils.getIfExists(errors, [
                    'settings',
                    'ALTERNATIVE_ANALYSIS',
                    'cluster_days',
                    'messages',
                  ])
                "
                :label="schema.ALTERNATIVE_ANALYSIS.properties.cluster_days.title"
                :hint="schema.ALTERNATIVE_ANALYSIS.properties.cluster_days.tooltip"
                min="1"
                max="30"
                :class="`my-${inputMargin}`"
                @change="$emit('change')"
                thumb-label="always"
              ></v-slider>
              <v-slider
                v-model="computedSettings.ALTERNATIVE_ANALYSIS.simulation_years_each"
                :label="`Each simulation year should account for ${settings.ALTERNATIVE_ANALYSIS.simulation_years_each} years`"
                :hint="schema.ALTERNATIVE_ANALYSIS.properties.simulation_years_each.tooltip"
                :error-messages="
                  $utils.getIfExists(errors, [
                    'settings',
                    'ALTERNATIVE_ANALYSIS',
                    'simulation_years_each',
                    'messages',
                  ])
                "
                min="1"
                max="20"
                always-dirty
                thumb-label="always"
                :class="`my-${inputMargin}`"
                @change="$emit('change')"
              ></v-slider>
              <v-switch
                label="Keep previous outputs when case is saved"
                v-model="computedSettings.ALTERNATIVE_ANALYSIS.saveOutputs"
                :error-messages="
                  $utils.getIfExists(errors, [
                    'settings',
                    'ALTERNATIVE_ANALYSIS',
                    'saveOutputs',
                    'messages',
                  ])
                "
                hint="WARNING: Enabling this can cause discrepancies between the stored input and the resulting outputs"
                persistent-hint
                :class="`my-${inputMargin}`"
                @change="$emit('change')"
              ></v-switch> </v-card-text
          ></v-card>
        </v-card>
      </v-tab-item>
      <v-tab-item v-if="computedSettings.PRODUCTION_COST.active" key="Production Cost">
        <v-card class="d-flex justify-center" flat>
          <v-card class="pa-2 ma-2" outlined width="700">
            <v-card-text>
              <v-switch
                label="Enable MIP"
                hint="Binary Variables"
                persistent-hint
                v-model="computedSettings.PRODUCTION_COST.integer"
                :error-messages="
                  $utils.getIfExists(errors, ['settings', 'PRODUCTION_COST', 'integer', 'messages'])
                "
                :class="`my-${inputMargin}`"
                @change="$emit('change')"
              ></v-switch>
              <v-switch
                v-model="computedSettings.PRODUCTION_COST.output_formulation"
                :label="schema.PRODUCTION_COST.properties.output_formulation.title"
                :hint="schema.PRODUCTION_COST.properties.output_formulation.tooltip"
                persistent-hint
                :error-messages="
                  $utils.getIfExists(errors, [
                    'settings',
                    'PRODUCTION_COST',
                    'output_formulation',
                    'messages',
                  ])
                "
                :class="`my-${inputMargin}`"
                @change="$emit('change')"
              ></v-switch>
              <v-switch
                :label="
                  settings.PRODUCTION_COST.subhourly ? 'Calculate Subhourly' : 'Calculate Hourly'
                "
                v-model="computedSettings.PRODUCTION_COST.subhourly"
                :error-messages="
                  $utils.getIfExists(errors, [
                    'settings',
                    'PRODUCTION_COST',
                    'subhourly',
                    'messages',
                  ])
                "
                :class="`my-${inputMargin}`"
                @change="$emit('change')"
              ></v-switch>
              <v-slider
                v-model.number="computedSettings.PRODUCTION_COST.tolerance"
                :label="`Tolerance: 1e${settings.PRODUCTION_COST.tolerance}`"
                :hint="schema.PRODUCTION_COST.properties.tolerance.tooltip"
                :error-messages="
                  $utils.getIfExists(errors, [
                    'settings',
                    'PRODUCTION_COST',
                    'tolerance',
                    'messages',
                  ])
                "
                max="-1"
                min="-38"
                always-dirty
                thumb-label="always"
                :class="`my-${inputMargin}`"
                @change="$emit('change')"
              ></v-slider>
              <InputTextField
                v-model.number="computedSettings.PRODUCTION_COST.timeout"
                :label="schema.PRODUCTION_COST.properties.timeout.title"
                :hint="schema.PRODUCTION_COST.properties.timeout.tooltip"
                :error-messages="
                  $utils.getIfExists(errors, ['settings', 'PRODUCTION_COST', 'timeout', 'messages'])
                "
                suffix="seconds"
                type="number"
                :class="`my-${inputMargin}`"
                @change="$emit('change')"
              ></InputTextField>
              <v-select
                v-model="computedSettings.PRODUCTION_COST.capacity_source"
                :label="schema.PRODUCTION_COST.properties.capacity_source.title"
                :hint="schema.PRODUCTION_COST.properties.capacity_source.tooltip"
                :error-messages="
                  $utils.getIfExists(errors, [
                    'settings',
                    'PRODUCTION_COST',
                    'capacity_source',
                    'messages',
                  ])
                "
                :items="['AA', 'Inputs']"
                :class="`my-${inputMargin}`"
                @change="$emit('change')"
              ></v-select>
              <v-switch
                label="Keep previous outputs when case is saved"
                v-model="computedSettings.PRODUCTION_COST.saveOutputs"
                :error-messages="
                  $utils.getIfExists(errors, [
                    'settings',
                    'PRODUCTION_COST',
                    'saveOutputs',
                    'messages',
                  ])
                "
                hint="WARNING: Enabling this can cause discrepancies between the stored input and the resulting outputs"
                persistent-hint
                :class="`my-${inputMargin}`"
                @change="$emit('change')"
              ></v-switch>
            </v-card-text>
          </v-card>
        </v-card>
      </v-tab-item>
      <v-tab-item v-if="computedSettings.NODAL_PRODUCTION_COST.active" key="Nodal Production Cost">
        <v-card class="d-flex justify-center" flat>
          <v-card class="pa-2 ma-2" outlined width="700">
            <v-card-text>
              <v-card flat class="mb-6">
                <p class="text-subtitle-1 font-weight-medium mb-1">Calculation Timeline</p>
                <p class="text-caption">Configure timeline to run the optimization</p>
                <v-row dense class="align-stretch">
                  <v-col cols="2">
                    <v-btn-toggle color="primary" exclusive mandatory v-model="npcTimelineOption">
                      <v-tooltip top>
                        <template #activator="{ on }">
                          <v-btn icon class="size-36" v-on="on" value="dates">
                            <v-icon>mdi-calendar</v-icon>
                          </v-btn>
                        </template>
                        <span>Choose a start and end date</span>
                      </v-tooltip>
                      <v-tooltip top>
                        <template #activator="{ on }">
                          <v-btn icon class="size-36" v-on="on" value="years">
                            <v-icon>mdi-calendar-multiselect</v-icon>
                          </v-btn>
                        </template>
                        <span>Pick specific years</span>
                      </v-tooltip>
                    </v-btn-toggle>
                  </v-col>
                  <template v-if="npcTimelineOption === 'dates'">
                    <v-col sm="5" class="pt-2">
                      <InputDateTime
                        v-model="computedSettings.NODAL_PRODUCTION_COST.timelineSettings.startDate"
                        label="Start Date"
                        timezone="UTC"
                        :minDate="systemDateConstraints.min"
                        :maxDate="systemDateConstraints.max"
                        dense
                        @change="$emit('change')"
                      />
                    </v-col>
                    <v-col sm="5" class="pt-2">
                      <InputDateTime
                        v-model="computedSettings.NODAL_PRODUCTION_COST.timelineSettings.endDate"
                        label="End Date"
                        timezone="UTC"
                        :maxDate="systemDateConstraints.max"
                        :minDate="computedSettings.NODAL_PRODUCTION_COST.timelineSettings.startDate"
                        dense
                        @change="$emit('change')"
                      />
                    </v-col>
                  </template>
                  <template v-if="npcTimelineOption === 'years'">
                    <v-col class="pt-2 ml-2">
                      <v-select
                        v-model="computedSettings.NODAL_PRODUCTION_COST.timelineSettings.years"
                        label="Years"
                        :items="yearItems"
                        dense
                        multiple
                        @change="$emit('change')"
                      ></v-select>
                    </v-col>
                  </template>
                </v-row>
                <v-divider></v-divider>
              </v-card>
              <v-card flat class="mb-6">
                <p class="text-subtitle-1 font-weight-medium mb-1">Inputs</p>
                <InputGeneric
                  :label="schema.NODAL_PRODUCTION_COST.properties.capacity_source.title"
                  :hint="schema.NODAL_PRODUCTION_COST.properties.capacity_source.tooltip"
                  class="mb-2"
                >
                  <v-select
                    v-model="computedSettings.NODAL_PRODUCTION_COST.capacity_source"
                    :items="schema.NODAL_PRODUCTION_COST.properties.capacity_source.enum"
                    @change="$emit('change')"
                  ></v-select>
                </InputGeneric>
                <v-divider></v-divider>
              </v-card>
              <v-card flat class="mb-6">
                <p class="text-subtitle-1 font-weight-medium mb-1">Outputs</p>
                <v-switch
                  label="Save timeseries outputs"
                  hint="This has an impact on running time and output file size"
                  persistent-hint
                  v-model="computedSettings.NODAL_PRODUCTION_COST.save_time_series_outputs"
                  dense
                  class="mb-4"
                  @change="$emit('change')"
                ></v-switch>
                <v-divider></v-divider>
              </v-card>
              <v-card flat class="mb-6">
                <p class="text-subtitle-1 font-weight-medium mb-1">Solver Settings</p>
                <v-switch
                  v-model="computedSettings.NODAL_PRODUCTION_COST.infeasibility_analysis"
                  label="Infeasibility Analysis"
                  hint="Try to detect the constraints causing infeasibilities. May change results slightly"
                  persistent-hint
                  @change="$emit('change')"
                ></v-switch>
                <v-switch
                  label="Enable MIP"
                  hint="Binary Variables. More accurate but slower"
                  persistent-hint
                  v-model="computedSettings.NODAL_PRODUCTION_COST.integer"
                  dense
                  @change="$emit('change')"
                ></v-switch>
                <v-switch
                  :label="
                    !settings.NODAL_PRODUCTION_COST.subhourly
                      ? 'Calculate Subhourly'
                      : 'Calculate Hourly'
                  "
                  v-model="computedSettings.NODAL_PRODUCTION_COST.subhourly"
                  :error-messages="
                    $utils.getIfExists(errors, [
                      'settings',
                      'NODAL_PRODUCTION_COST',
                      'subhourly',
                      'messages',
                    ])
                  "
                  :class="`my-${inputMargin}`"
                  dense
                  @change="$emit('change')"
                ></v-switch>
                <v-slider
                  v-model.number="computedSettings.NODAL_PRODUCTION_COST.tolerance"
                  :label="`Tolerance: 1e${settings.NODAL_PRODUCTION_COST.tolerance}`"
                  max="-1"
                  min="-38"
                  always-dirty
                  thumb-label="always"
                  :class="`my-${inputMargin}`"
                  @change="$emit('change')"
                ></v-slider>
                <InputTextField
                  v-model.number="computedSettings.NODAL_PRODUCTION_COST.timeout"
                  :label="schema.NODAL_PRODUCTION_COST.properties.timeout.title"
                  :hint="schema.NODAL_PRODUCTION_COST.properties.timeout.tooltip"
                  :error-messages="
                    $utils.getIfExists(errors, [
                      'settings',
                      'NODAL_PRODUCTION_COST',
                      'timeout',
                      'messages',
                    ])
                  "
                  suffix="seconds"
                  type="number"
                  @change="$emit('change')"
                ></InputTextField>
              </v-card>
            </v-card-text>
          </v-card>
        </v-card>
      </v-tab-item>
      <v-tab-item
        v-if="computedSettings.STACKED_SERVICES_EMULATOR.active"
        key="Stacked Services Emulator"
      >
        <v-card class="d-flex justify-center" flat>
          <v-card class="pa-2 ma-2" outlined width="700">
            <v-card-text>
              <p class="text-subtitle-1 font-weight-medium mb-1">Calculation Timeline</p>
              <p class="text-caption">Configure timeline to run the optimization</p>
              <v-row dense class="align-stretch">
                <v-col cols="2">
                  <v-btn-toggle color="primary" exclusive mandatory v-model="sseTimelineOption">
                    <v-tooltip top>
                      <template #activator="{ on }">
                        <v-btn icon class="size-36" v-on="on" value="dates">
                          <v-icon>mdi-calendar</v-icon>
                        </v-btn>
                      </template>
                      <span>Choose a start and end date</span>
                    </v-tooltip>
                    <v-tooltip top>
                      <template #activator="{ on }">
                        <v-btn icon class="size-36" v-on="on" value="years">
                          <v-icon>mdi-calendar-multiselect</v-icon>
                        </v-btn>
                      </template>
                      <span>Pick specific years</span>
                    </v-tooltip>
                  </v-btn-toggle>
                </v-col>
                <template v-if="sseTimelineOption === 'dates'">
                  <v-col sm="5" class="pt-2">
                    <InputDateTime
                      v-model="computedSettings.timeline_settings.start_date"
                      label="Start Date"
                      timezone="UTC"
                      :minDate="systemDateConstraints.min"
                      :maxDate="systemDateConstraints.max"
                      dense
                      @change="$emit('change')"
                    />
                  </v-col>
                  <v-col sm="5" class="pt-2">
                    <InputDateTime
                      v-model="computedSettings.timeline_settings.end_date"
                      label="End Date"
                      timezone="UTC"
                      :maxDate="systemDateConstraints.max"
                      :minDate="computedSettings.timeline_settings.start_date"
                      dense
                      @change="$emit('change')"
                    />
                  </v-col>
                </template>
                <template v-if="sseTimelineOption === 'years'">
                  <v-col class="pt-2 ml-2">
                    <v-select
                      v-model="computedSettings.timeline_settings.years"
                      label="Years"
                      :items="yearItems"
                      dense
                      multiple
                      @change="$emit('change')"
                    ></v-select>
                  </v-col>
                </template>
              </v-row>
              <v-divider></v-divider>
              <v-card flat class="mb-6">
                <p class="text-subtitle-1 font-weight-medium">Optimization Settings</p>
              </v-card>
              <v-switch
                v-model="computedSettings.STACKED_SERVICES_EMULATOR.infeasibility_analysis"
                label="Infeasibility Analysis"
                hint="Try to detect the constraints causing infeasibilities. May change results slightly"
                persistent-hint
                @change="$emit('change')"
              ></v-switch>
              <InputTextField
                v-model.number="computedSettings.timeline_settings.time_interval"
                :label="schema.timeline_settings.properties.time_interval.title"
                hint="The duration between successive energy dispatch calculations"
                :suffix="schema.timeline_settings.properties.time_interval.unit"
                type="number"
                dense
                @change="$emit('change')"
              ></InputTextField>
              <InputTextField
                v-model.number="computedSettings.timeline_settings.opt_window_lookahead"
                :label="schema.timeline_settings.properties.lookahead.title"
                hint="The future time window considered in optimization to anticipate system conditions and plan dispatch decisions."
                :suffix="schema.timeline_settings.properties.lookahead.unit"
                type="number"
                dense
                @change="$emit('change')"
              ></InputTextField>
              <InputTextField
                v-model.number="computedSettings.timeline_settings.opt_window"
                :label="'Optimization Window'"
                hint="The total time horizon over which the optimization is performed during each simulation step"
                :suffix="schema.timeline_settings.properties.opt_window.unit"
                type="number"
                dense
                @change="$emit('change')"
              ></InputTextField>
              <InputTextField
                v-model.number="computedSettings.timeline_settings.padding"
                :label="schema.timeline_settings.properties.padding.title"
                :hint="schema.timeline_settings.properties.padding.tooltip"
                type="number"
                dense
                @change="$emit('change')"
              ></InputTextField>
              <v-switch
                label="Enable MIP"
                hint="Binary Variables"
                persistent-hint
                v-model="computedSettings.STACKED_SERVICES_EMULATOR.integer"
                :error-messages="
                  $utils.getIfExists(errors, [
                    'settings',
                    'STACKED_SERVICES_EMULATOR',
                    'integer',
                    'messages',
                  ])
                "
                :class="`my-${inputMargin}`"
                dense
                @change="$emit('change')"
              ></v-switch>
              <v-switch
                v-model="computedSettings.STACKED_SERVICES_EMULATOR.output_formulation"
                :label="sseSchema.output_formulation.title"
                :hint="sseSchema.output_formulation.tooltip"
                persistent-hint
                :error-messages="
                  $utils.getIfExists(errors, [
                    'settings',
                    'STACKED_SERVICES_EMULATOR',
                    'output_formulation',
                    'messages',
                  ])
                "
                :class="`my-${inputMargin}`"
                dense
                @change="$emit('change')"
              ></v-switch>
              <v-slider
                v-model.number="
                  computedSettings.STACKED_SERVICES_EMULATOR.solver_settings.tolerance
                "
                :label="`Tolerance: 1e${settings.STACKED_SERVICES_EMULATOR.solver_settings.tolerance}`"
                :hint="sseSchema.solver_settings.properties.tolerance.tooltip"
                :error-messages="
                  $utils.getIfExists(errors, [
                    'settings',
                    'STACKED_SERVICES_EMULATOR',
                    'tolerance',
                    'messages',
                  ])
                "
                max="-1"
                min="-38"
                always-dirty
                thumb-label="always"
                :class="`my-${inputMargin}`"
                @change="$emit('change')"
              ></v-slider>
              <InputTextField
                v-model.number="computedSettings.STACKED_SERVICES_EMULATOR.solver_settings.timeout"
                :label="sseSchema.solver_settings.properties.timeout.title"
                :hint="sseSchema.solver_settings.properties.timeout.tooltip"
                :error-messages="
                  $utils.getIfExists(errors, [
                    'settings',
                    'STACKED_SERVICES_EMULATOR',
                    'timeout',
                    'messages',
                  ])
                "
                suffix="seconds"
                type="number"
                :class="`my-${inputMargin}`"
                @change="$emit('change')"
              ></InputTextField>
              <v-select
                v-model="computedSettings.STACKED_SERVICES_EMULATOR.solver_settings.direction"
                label="Direction"
                :hint="sseSchema.solver_settings.properties.direction.tooltip"
                :items="sseSchema.solver_settings.properties.direction.enum"
                :class="`my-${inputMargin}`"
                @change="$emit('change')"
              ></v-select>
              <InputToggleParam
                v-model="computedSettings.STACKED_SERVICES_EMULATOR.sequencing"
                :error-messages="
                  $utils.getIfExists(errors, [
                    'settings',
                    'STACKED_SERVICES_EMULATOR',
                    'sequencing',
                    'messages',
                  ])
                "
                label="Sequencing"
                @change="$emit('change')"
              >
                <template v-slot="props">
                  <InputPrice
                    v-model="props.param"
                    :num_years="num_years"
                    :reserves="input.reserves"
                    :sequencing="{
                      active: !props.disabled,
                      allowedSources: ['Market', 'Custom'],
                      allowedSchedules: ['RT'],
                    }"
                    multi-year
                    node-list
                    @change="$emit('change')"
                  ></InputPrice>
                </template>
              </InputToggleParam>
              <v-switch
                :label="sseSchema.save_time_series_outputs.title"
                v-model="computedSettings.STACKED_SERVICES_EMULATOR.save_time_series_outputs"
                :hint="sseSchema.save_time_series_outputs.tooltip"
                persistent-hint
                dense
                @change="$emit('change')"
              ></v-switch>
              <v-switch
                label="Keep previous outputs when case is saved"
                v-model="computedSettings.STACKED_SERVICES_EMULATOR.saveOutputs"
                :error-messages="
                  $utils.getIfExists(errors, [
                    'settings',
                    'STACKED_SERVICES_EMULATOR',
                    'saveOutputs',
                    'messages',
                  ])
                "
                hint="WARNING: Enabling this can cause discrepancies between the stored input and the resulting outputs"
                persistent-hint
                :class="`my-${inputMargin}`"
                dense
                @change="$emit('change')"
              ></v-switch>
            </v-card-text>
          </v-card>
        </v-card>
      </v-tab-item>
      <v-tab-item v-if="computedSettings.ELCC.active" key="ELCC">
        <v-card class="d-flex justify-center" flat>
          <v-card class="pa-2 ma-2" outlined width="700">
            <v-card-text>
              <v-select
                v-model="computedSettings.ELCC.methodology"
                label="Methodology"
                :items="elccSchema.methodology.enum"
                @change="$emit('change')"
              >
              </v-select>
              <v-switch
                v-model="computedSettings.ELCC.threshold_active"
                :label="elccSchema.threshold_active.title"
                :hint="elccSchema.threshold_active.tooltip"
                persistent-hint
                dense
                @change="$emit('change')"
              ></v-switch>
              <InputTextField
                v-model.number="computedSettings.ELCC.target_lole"
                :label="elccSchema.target_lole.title"
                :hint="elccSchema.target_lole.tooltip"
                type="number"
                :suffix="elccSchema.target_lole.unit"
                :min="elccSchema.target_lole.minimum"
                :max="elccSchema.target_lole.maximum"
                @change="$emit('change')"
                :class="`my-${inputMargin}`"
              ></InputTextField>
              <InputTextField
                v-model.number="computedSettings.ELCC.tolerance_lole"
                :label="elccSchema.tolerance_lole.title"
                :suffix="elccSchema.tolerance_lole.unit"
                :hint="elccSchema.tolerance_lole.tooltip"
                :min="elccSchema.tolerance_lole.minimum"
                :max="elccSchema.tolerance_lole.maximum"
                type="number"
                @change="$emit('change')"
              ></InputTextField>
              <InputTextField
                v-model.number="computedSettings.ELCC.load_step"
                :label="elccSchema.load_step.title"
                :suffix="elccSchema.load_step.unit"
                :hint="elccSchema.load_step.tooltip"
                :min="elccSchema.load_step.minimum"
                :max="elccSchema.load_step.maximum"
                type="number"
                @change="$emit('change')"
              ></InputTextField>
              <InputTextField
                v-model.number="computedSettings.ELCC.load_ratio"
                :label="elccSchema.load_ratio.title"
                :suffix="elccSchema.load_ratio.unit"
                :hint="elccSchema.load_ratio.tooltip"
                :min="elccSchema.load_ratio.minimum"
                :max="elccSchema.load_ratio.maximum"
                :type="elccSchema.load_ratio.type"
                @change="$emit('change')"
              ></InputTextField>
              <v-switch
                v-model="computedSettings.ELCC.save_pc_outputs"
                :label="elccSchema.save_pc_outputs.title"
                :hint="elccSchema.save_pc_outputs.tooltip"
                persistent-hint
                :class="`my-${inputMargin}`"
                dense
                @change="$emit('change')"
              ></v-switch>
              <InputTextField
                v-if="settings.ELCC.methodology !== 'Net-Load relative to Load'"
                v-model.number="computedSettings.ELCC.peaker_step"
                :label="elccSchema.peaker_step.title"
                :suffix="elccSchema.peaker_step.unit"
                :hint="elccSchema.peaker_step.tooltip"
                :min="elccSchema.peaker_step.minimum"
                :max="elccSchema.peaker_step.maximum"
                type="number"
                @change="$emit('change')"
              ></InputTextField>
              <v-switch
                label="Keep previous outputs when case is saved"
                v-model="computedSettings.ELCC.saveOutputs"
                :error-messages="
                  $utils.getIfExists(errors, ['settings', 'ELCC', 'saveOutputs', 'messages'])
                "
                hint="WARNING: Enabling this can cause discrepancies between the stored input and the resulting outputs"
                persistent-hint
                :class="`my-${inputMargin}`"
                dense
                @change="$emit('change')"
              ></v-switch>
            </v-card-text>
          </v-card>
        </v-card>
      </v-tab-item>
      <v-tab-item v-if="computedSettings.POWER_FLOW.active" key="Power Flow">
        <v-card class="d-flex justify-center" flat>
          <v-card class="pa-2 ma-2" outlined width="700">
            <v-card-text>
              <v-row>
                <v-col sm="4">
                  <InputDateTime
                    v-model="computedSettings.POWER_FLOW.start_datetime"
                    label="Start Date"
                    timezone="UTC"
                    :minDate="systemDateConstraints.min"
                    :maxDate="systemDateConstraints.max"
                    @change="$emit('change')"
                  />
                </v-col>
                <v-col sm="4">
                  <InputDateTime
                    v-model="computedSettings.POWER_FLOW.end_datetime"
                    label="End Date"
                    timezone="UTC"
                    :maxDate="systemDateConstraints.max"
                    :minDate="computedSettings.POWER_FLOW.start_datetime"
                    @change="$emit('change')"
                  />
                </v-col>
                <v-col sm="4">
                  <InputTextField
                    v-model.number="computedSettings.POWER_FLOW.time_interval"
                    :label="schema.POWER_FLOW.properties.time_interval.title"
                    :suffix="schema.POWER_FLOW.properties.time_interval.unit"
                    @change="$emit('change')"
                  ></InputTextField>
                </v-col>
              </v-row>

              <v-switch
                label="Run Contingency Analysis"
                v-model="computedSettings.POWER_FLOW.run_contingency"
                @change="$emit('change')"
              ></v-switch>
              <v-select
                v-if="computedSettings.POWER_FLOW.run_contingency"
                :items="contingencyTimeOptions"
                label="Time for contingency run"
                v-model="computedSettings.POWER_FLOW.contingency_time"
              ></v-select>
              <v-select
                v-model="computedSettings.POWER_FLOW.source"
                :label="schema.POWER_FLOW.properties.source.title"
                :items="schema.POWER_FLOW.properties.source.enum"
                item-text="text"
                @change="$emit('change')"
              ></v-select>
              <v-select
                v-model="computedSettings.POWER_FLOW.algorithm"
                :label="schema.POWER_FLOW.properties.algorithm.title"
                :hint="schema.POWER_FLOW.properties.algorithm.tooltip"
                :items="powerFlowAlgorithmItems"
                item-text="text"
                @change="$emit('change')"
              ></v-select>
              <v-select
                v-model="computedSettings.POWER_FLOW.calculate_voltage_angles"
                :label="schema.POWER_FLOW.properties.calculate_voltage_angles.title"
                :hint="schema.POWER_FLOW.properties.calculate_voltage_angles.tooltip"
                :items="schema.POWER_FLOW.properties.calculate_voltage_angles.enum"
                @change="$emit('change')"
              ></v-select>
              <v-select
                v-model="computedSettings.POWER_FLOW.initialization"
                :label="schema.POWER_FLOW.properties.initialization.title"
                :hint="schema.POWER_FLOW.properties.initialization.tooltip"
                :items="schema.POWER_FLOW.properties.initialization.enum"
                @change="$emit('change')"
              ></v-select>
              <InputTextField
                v-model.number="computedSettings.POWER_FLOW.max_iteration"
                :label="schema.POWER_FLOW.properties.max_iteration.title"
                :hint="schema.POWER_FLOW.properties.max_iteration.tooltip"
                @change="$emit('change')"
              ></InputTextField>
              <v-slider
                v-model.number="computedSettings.POWER_FLOW.tolerance"
                :label="`Tolerance: 1e${computedSettings.POWER_FLOW.tolerance}`"
                max="-1"
                min="-38"
                always-dirty
                thumb-label="always"
                :class="`my-${inputMargin}`"
                @change="$emit('change')"
              ></v-slider>
              <v-select
                v-model="computedSettings.POWER_FLOW.trafo_model"
                :label="schema.POWER_FLOW.properties.trafo_model.title"
                :hint="schema.POWER_FLOW.properties.trafo_model.tooltip"
                :items="powerFlowTransformerModelItems"
                @change="$emit('change')"
              ></v-select>
              <v-select
                v-model="computedSettings.POWER_FLOW.trafo_loading"
                :label="schema.POWER_FLOW.properties.trafo_loading.title"
                :hint="schema.POWER_FLOW.properties.trafo_loading.tooltip"
                :items="schema.POWER_FLOW.properties.trafo_loading.enum"
                @change="$emit('change')"
              ></v-select>
              <v-switch
                :label="schema.POWER_FLOW.properties.check_connectivity.title"
                v-model="computedSettings.POWER_FLOW.check_connectivity"
                dense
                @change="$emit('change')"
              ></v-switch>
              <v-switch
                :label="schema.POWER_FLOW.properties.voltage_depend_loads.title"
                v-model="computedSettings.POWER_FLOW.voltage_depend_loads"
                dense
                @change="$emit('change')"
              ></v-switch>
              <InputTextField
                v-model.number="computedSettings.POWER_FLOW.system_frequency"
                :label="schema.POWER_FLOW.properties.system_frequency.title"
                :suffix="schema.POWER_FLOW.properties.system_frequency.unit"
                :hint="schema.POWER_FLOW.properties.system_frequency.tooltip"
                @change="$emit('change')"
              ></InputTextField>
              <InputTextField
                v-model.number="computedSettings.POWER_FLOW.reference_apparent_power"
                :label="schema.POWER_FLOW.properties.reference_apparent_power.title"
                :suffix="schema.POWER_FLOW.properties.reference_apparent_power.unit"
                :hint="schema.POWER_FLOW.properties.reference_apparent_power.tooltip"
                @change="$emit('change')"
              ></InputTextField>
              <InputTextField
                v-model.number="computedSettings.POWER_FLOW.node_upper_threshold_voltage"
                :label="schema.POWER_FLOW.properties.node_upper_threshold_voltage.title"
                :suffix="schema.POWER_FLOW.properties.node_upper_threshold_voltage.unit"
                @change="$emit('change')"
              ></InputTextField>
              <InputTextField
                v-model.number="computedSettings.POWER_FLOW.node_lower_threshold_voltage"
                :label="schema.POWER_FLOW.properties.node_lower_threshold_voltage.title"
                :suffix="schema.POWER_FLOW.properties.node_lower_threshold_voltage.unit"
                @change="$emit('change')"
              ></InputTextField>
              <InputTextField
                v-model.number="computedSettings.POWER_FLOW.trafo_percent_rating"
                :label="schema.POWER_FLOW.properties.trafo_percent_rating.title"
                :suffix="schema.POWER_FLOW.properties.trafo_percent_rating.unit"
                @change="$emit('change')"
              ></InputTextField>
              <InputTextField
                v-model.number="computedSettings.POWER_FLOW.line_percent_rating"
                :label="schema.POWER_FLOW.properties.line_percent_rating.title"
                :suffix="schema.POWER_FLOW.properties.line_percent_rating.unit"
                @change="$emit('change')"
              ></InputTextField>
              <InputTextField
                v-model.number="computedSettings.POWER_FLOW.gen_power_factor_threshold"
                label="Generator Power Factor Threshold"
                @change="$emit('change')"
              ></InputTextField>
              <v-switch
                label="Keep previous outputs when case is saved"
                v-model="computedSettings.POWER_FLOW.saveOutputs"
                :error-messages="
                  $utils.getIfExists(errors, ['settings', 'POWER_FLOW', 'saveOutputs', 'messages'])
                "
                hint="WARNING: Enabling this can cause discrepancies between the stored input and the resulting outputs"
                persistent-hint
                :class="`my-${inputMargin}`"
                dense
                @change="$emit('change')"
              ></v-switch>
            </v-card-text>
          </v-card>
        </v-card>
      </v-tab-item>
      <v-tab-item v-if="computedSettings.SHORT_CIRCUIT.active" key="Short Circuit">
        <v-card class="d-flex justify-center" flat>
          <v-card class="pa-2 ma-2" width="700">
            <v-card-text>
              <v-select
                v-model="computedSettings.SHORT_CIRCUIT.current_calculation"
                :label="schema.SHORT_CIRCUIT.properties.current_calculation.title"
                :hint="schema.SHORT_CIRCUIT.properties.current_calculation.tooltip"
                :items="schema.SHORT_CIRCUIT.properties.current_calculation.enum"
                @change="$emit('change')"
              ></v-select>
              <InputTextField
                v-model.number="computedSettings.SHORT_CIRCUIT.low_voltage_grid_tolerance"
                :suffix="schema.SHORT_CIRCUIT.properties.low_voltage_grid_tolerance.unit"
                :label="schema.SHORT_CIRCUIT.properties.low_voltage_grid_tolerance.title"
                :hint="schema.SHORT_CIRCUIT.properties.low_voltage_grid_tolerance.tooltip"
                @change="$emit('change')"
              ></InputTextField>
              <v-select
                v-model="computedSettings.SHORT_CIRCUIT.topology"
                :disabled="disableTopology"
                :label="schema.SHORT_CIRCUIT.properties.topology.title"
                :hint="schema.SHORT_CIRCUIT.properties.topology.tooltip"
                :items="schema.SHORT_CIRCUIT.properties.topology.enum"
                @change="$emit('change')"
              ></v-select>
              <v-switch
                :label="schema.SHORT_CIRCUIT.properties.aperiodic_SC_current.title"
                v-model="computedSettings.SHORT_CIRCUIT.aperiodic_SC_current"
                dense
                @change="$emit('change')"
              ></v-switch>
              <v-switch
                :label="schema.SHORT_CIRCUIT.properties.equivalent_thermical_SC_current.title"
                v-model="computedSettings.SHORT_CIRCUIT.equivalent_thermical_SC_current"
                dense
                @change="$emit('change')"
              ></v-switch>
              <InputTextField
                v-model.number="computedSettings.SHORT_CIRCUIT.failure_clearing_time"
                :disabled="disableFailureClearingTime"
                :suffix="schema.SHORT_CIRCUIT.properties.failure_clearing_time.unit"
                :label="schema.SHORT_CIRCUIT.properties.failure_clearing_time.title"
                :hint="schema.SHORT_CIRCUIT.properties.failure_clearing_time.tooltip"
                @change="$emit('change')"
              ></InputTextField>
              <InputTextField
                v-model.number="computedSettings.SHORT_CIRCUIT.fault_resistance"
                :suffix="schema.SHORT_CIRCUIT.properties.fault_resistance.unit"
                :label="schema.SHORT_CIRCUIT.properties.fault_resistance.title"
                :hint="schema.SHORT_CIRCUIT.properties.fault_resistance.tooltip"
                @change="$emit('change')"
              ></InputTextField>
              <InputTextField
                v-model.number="computedSettings.SHORT_CIRCUIT.fault_reactance"
                :suffix="schema.SHORT_CIRCUIT.properties.fault_reactance.unit"
                :label="schema.SHORT_CIRCUIT.properties.fault_reactance.title"
                :hint="schema.SHORT_CIRCUIT.properties.fault_reactance.tooltip"
                @change="$emit('change')"
              ></InputTextField>
              <v-switch
                :label="schema.SHORT_CIRCUIT.properties.branch_results.title"
                v-model="computedSettings.SHORT_CIRCUIT.branch_results"
                dense
                @change="$emit('change')"
              ></v-switch>
              <v-switch
                :label="schema.SHORT_CIRCUIT.properties.return_all_currents.title"
                :disabled="disableReturnAllCurrents"
                v-model="computedSettings.SHORT_CIRCUIT.return_all_currents"
                dense
                @change="$emit('change')"
              ></v-switch>
              <v-select
                v-model="computedSettings.SHORT_CIRCUIT.bus"
                :label="schema.SHORT_CIRCUIT.properties.bus.title"
                :hint="schema.SHORT_CIRCUIT.properties.bus.tooltip"
                :items="nodeIndex"
                multiple
                @change="$emit('change')"
              >
                <template #selection="data">
                  {{ data.item.name }}
                  <span class="text-caption ml-2">{{ `(ID: ${data.item.value})` }}</span>
                </template>
                <template #item="data">
                  {{ data.item.name }}
                  <span class="text-caption ml-2">{{ `(ID: ${data.item.value})` }}</span>
                </template>
              </v-select>
              <v-switch
                label="Keep previous outputs when case is saved"
                v-model="computedSettings.SHORT_CIRCUIT.saveOutputs"
                :error-messages="
                  $utils.getIfExists(errors, [
                    'settings',
                    'SHORT_CIRCUIT',
                    'saveOutputs',
                    'messages',
                  ])
                "
                hint="WARNING: Enabling this can cause discrepancies between the stored input and the resulting outputs"
                persistent-hint
                :class="`my-${inputMargin}`"
                dense
                @change="$emit('change')"
              ></v-switch>
            </v-card-text>
          </v-card>
        </v-card>
      </v-tab-item>
      <v-tab-item v-if="computedSettings.TRANSIENT_STABILITY.active" key="Transient Stability">
        <v-card class="d-flex justify-center" flat>
          <v-card class="pa-2 ma-2" width="700">
            <v-row>
              <v-col sm="4">
                <InputDateTime
                  v-model="computedSettings.TRANSIENT_STABILITY.start_datetime"
                  label="Start Date"
                  timezone="UTC"
                  :minDate="systemDateConstraints.min"
                  :maxDate="systemDateConstraints.max"
                  @change="$emit('change')"
                />
              </v-col>
              <v-col sm="4">
                <InputDateTime
                  v-model="computedSettings.TRANSIENT_STABILITY.end_datetime"
                  label="End Date"
                  timezone="UTC"
                  :maxDate="systemDateConstraints.max"
                  :minDate="computedSettings.TRANSIENT_STABILITY.start_datetime"
                  @change="$emit('change')"
                />
              </v-col>
              <v-col sm="4">
                <InputTextField
                  v-model.number="computedSettings.TRANSIENT_STABILITY.simulate_every"
                  label="Simulate every"
                  suffix="min"
                  @change="$emit('change')"
                ></InputTextField>
              </v-col>
            </v-row>
            <p>{{ transientTimeParams }}</p>

            <v-select
              v-model="computedSettings.TRANSIENT_STABILITY.simulation_source"
              label="Simulation source"
              :items="tStabilitySources"
              dense
              @change="$emit('change')"
            ></v-select>

            <template
              v-if="computedSettings.TRANSIENT_STABILITY.simulation_source === 'CASE_TABLE'"
            >
              <InputProfile
                class="mb-4"
                v-model="computedSettings.TRANSIENT_STABILITY.simulation_table"
                profile-type="SIMULATION_TABLE"
                label="Simulations table"
                hint="One row per simulation"
                object-value
                :use-headers="[
                  'inertia',
                  'max_generator_generation',
                  'machine_generation',
                  'max_power_generation',
                  'min_power_generation',
                  'generators_reg_up',
                  'generators_reg_down',
                  'droop',
                  'ramp_capability',
                  'max_battery_cap',
                  'battery_pow_nom',
                  'battery_reg_up',
                  'battery_reg_down',
                ]"
                @change="$emit('change')"
              ></InputProfile>
              <InputYearly
                v-model="computedSettings.TRANSIENT_STABILITY.base_power"
                label="Base Power"
                suffix="MVA"
                type="number"
                :num_years="num_years"
                fill
                :fill-value="0"
                @change="$emit('change')"
              ></InputYearly>
            </template>
            <template v-else>
              <p class="text-caption">
                Use SSE outputs for transient stability analysis. Remember to set inertia and droop
                parameters. Only the first project will be used.
              </p>
            </template>

            <v-select
              v-model="computedSettings.TRANSIENT_STABILITY.include_simulations"
              label="Include individual simulations in the output"
              :items="tStabilityIncludeSimulations"
              @change="$emit('change')"
            ></v-select>
            <InputTextField
              v-model.number="computedSettings.TRANSIENT_STABILITY.time_step"
              hint="Time interval for transient simulation calculations"
              label="Time Step"
              suffix="s"
              @change="$emit('change')"
            ></InputTextField>
            <v-divider></v-divider>
            <InputTextField
              v-model.number="computedSettings.TRANSIENT_STABILITY.base_frequency"
              label="Base Frequency"
              suffix="Hz"
              hint="System Frequency"
              @change="$emit('change')"
            ></InputTextField>
            <InputTextField
              v-model.number="computedSettings.TRANSIENT_STABILITY.equivalent_damping_factor"
              label="Equivalent Damping Factor"
              suffix="pu"
              hint="Measure of system oscillation reduction"
              @change="$emit('change')"
            ></InputTextField>

            <!-- Generator Response -->
            <v-switch
              label="Generator Response"
              hint="Use generators for frequency response"
              persistent-hint
              v-model="computedSettings.TRANSIENT_STABILITY.generator_response.active"
              dense
              @change="$emit('change')"
            ></v-switch>
            <template v-if="computedSettings.TRANSIENT_STABILITY.generator_response.active">
              <InputTextField
                v-model.number="computedSettings.TRANSIENT_STABILITY.generator_response.delay"
                label="Delay"
                suffix="s"
                hint="Lag in transient stability response"
                @change="$emit('change')"
              ></InputTextField>
              <InputTextField
                v-model.number="computedSettings.TRANSIENT_STABILITY.generator_response.deadband"
                label="Dead Band"
                suffix="mHz"
                hint="Range where generator ignores small signal changes"
                @change="$emit('change')"
              ></InputTextField>
              <InputTextField
                v-model.number="computedSettings.TRANSIENT_STABILITY.generator_response.lead_time"
                label="Lead Time Constant"
                hint="Speed of initial generator control response"
                suffix="s"
                @change="$emit('change')"
              ></InputTextField>
              <InputTextField
                v-model.number="computedSettings.TRANSIENT_STABILITY.generator_response.lag_time"
                label="Lag Time Constant"
                suffix="s"
                hint="Time to reach steady state after response has started"
                @change="$emit('change')"
              ></InputTextField>
              <v-divider></v-divider>
            </template>

            <!-- BESS Response -->
            <v-switch
              label="BESS Response"
              hint="Use BESS for frequency response"
              persistent-hint
              v-model="computedSettings.TRANSIENT_STABILITY.bess_response.active"
              dense
              @change="$emit('change')"
            ></v-switch>
            <template v-if="computedSettings.TRANSIENT_STABILITY.bess_response.active">
              <InputTextField
                v-model.number="computedSettings.TRANSIENT_STABILITY.bess_response.delay"
                label="Delay"
                suffix="s"
                hint="Delay for BESS response"
                @change="$emit('change')"
              ></InputTextField>
              <InputTextField
                v-model.number="computedSettings.TRANSIENT_STABILITY.bess_response.deadband"
                label="Dead Band"
                suffix="mHz"
                hint="Deadband for BESS response"
                @change="$emit('change')"
              ></InputTextField>
              <InputProfile
                v-model="computedSettings.TRANSIENT_STABILITY.bess_response.freq_curve"
                profile-type="FREQUENCY_RESPONSE"
                hint="Response in percentage of storage power capacity"
                label="Frequency Response Curve"
                object-value
                :use-headers="['frequency', 'response']"
                @change="$emit('change')"
              ></InputProfile>
              <v-divider></v-divider>
            </template>

            <!-- UFLS Response -->
            <v-switch
              label="UFLS Response"
              hint="Load shedding under frequency"
              persistent-hint
              v-model="computedSettings.TRANSIENT_STABILITY.ufls_response.active"
              dense
              @change="$emit('change')"
            ></v-switch>
            <template v-if="computedSettings.TRANSIENT_STABILITY.ufls_response.active">
              <InputTextField
                v-model.number="computedSettings.TRANSIENT_STABILITY.ufls_response.delay"
                label="Delay"
                suffix="s"
                hint="Delay for UFLS"
                @change="$emit('change')"
              ></InputTextField>
              <InputProfile
                v-model="computedSettings.TRANSIENT_STABILITY.ufls_response.freq_curve"
                profile-type="FREQUENCY_RESPONSE"
                hint="Response in MW together with the frequency at which it its UFLS level gets activated"
                label="Frequency Response Curve"
                object-value
                :use-headers="['frequency', 'response']"
                @change="$emit('change')"
              ></InputProfile>
              <v-divider></v-divider>
            </template>
          </v-card>
        </v-card>
      </v-tab-item>
      <v-tab-item v-if="computedSettings.LOAD_FORECAST.active" key="Load Forecast">
        <v-card class="d-flex justify-center" flat>
          <v-card class="pa-2 ma-2" outlined width="700">
            <v-card-text>
              <v-card flat class="mb-6">
                <p class="text-subtitle-1 font-weight-medium mb-1">Timeline Configuration</p>
                <v-row dense class="my-4">
                  <v-col sm="4">
                    <v-text-field
                      v-model.number="computedSettings.LOAD_FORECAST.year_present"
                      label="Present Year"
                      hint="First year to be forecasted"
                      persistent-hint
                      type="number"
                      dense
                      @change="$emit('change')"
                    >
                    </v-text-field>
                  </v-col>
                  <v-col v-if="computedSettings.LOAD_FORECAST.year_present" sm="4">
                    <p class="text-caption">Historic Data Points: {{ historicDataPoints }}</p>
                    <p class="text-caption">
                      From {{ settings.year_start }} to
                      {{ settings.LOAD_FORECAST.year_present - 1 }}
                    </p>
                  </v-col>
                  <v-col v-if="computedSettings.LOAD_FORECAST.year_present" sm="4">
                    <p class="text-caption">Forecast Data Points: {{ forecastDataPoints }}</p>
                    <p class="text-caption">
                      From {{ settings.LOAD_FORECAST.year_present }} to {{ settings.year_end }}
                    </p>
                  </v-col>
                </v-row>
                <v-select
                  v-model="computedSettings.LOAD_FORECAST.granularity"
                  label="Granularity"
                  :items="['yearly', 'monthly']"
                  hint="Temporal granularity for the forecast"
                  persistent-hint
                  dense
                  @change="$emit('change')"
                ></v-select>
                <v-divider class="mt-4"></v-divider>
              </v-card>

              <v-card flat class="mb-6">
                <InputProfile
                  class="mb-4"
                  v-model="computedSettings.LOAD_FORECAST.historic_data"
                  profile-type="LF_HISTORIC_DATA"
                  label="Historic data"
                  :hint="`gdp, population, electric vehicles, energy intensity, total load. ${historicDataPoints} values expected`"
                  object-value
                  :use-headers="[
                    'gdp',
                    'population',
                    'electric vehicles',
                    'energy intensity',
                    'total load',
                  ]"
                  @change="$emit('change')"
                ></InputProfile>

                <InputProfile
                  class="mb-4"
                  v-model="computedSettings.LOAD_FORECAST.forecast_data"
                  profile-type="LF_FUTURE_DATA"
                  label="Future data"
                  :hint="`gdp, population, electric vehicles, energy intensity. ${forecastDataPoints} values expected`"
                  object-value
                  :use-headers="['gdp', 'population', 'electric vehicles', 'energy intensity']"
                  @change="$emit('change')"
                ></InputProfile>
              </v-card>

              <v-card flat class="mb-6">
                <p class="text-subtitle-1 font-weight-medium mb-1">
                  Peak Load Calculation Parameters
                </p>
                <p class="text-caption">
                  Formula:
                  <math>
                    <mrow>
                      <mi>Annual Peak Demand (MW)</mi>
                      <mo>=</mo>
                      <mi>α</mi>
                      <mo>⋅</mo>
                      <mi>Annual Energy Demand (GWh)</mi>
                      <mo>+</mo>
                      <mi>β</mi>
                      <mo>⋅</mo>
                      <msqrt>
                        <mi>Annual Energy Demand (GWh)</mi>
                      </msqrt>
                    </mrow>
                  </math>
                </p>

                <v-row>
                  <v-col sm="6">
                    <InputTextField
                      v-model.number="computedSettings.LOAD_FORECAST.alpha"
                      label="Alpha"
                      hint="Linear coefficient for peak load calculation"
                      type="number"
                      step="0.01"
                      @change="$emit('change')"
                    ></InputTextField>
                  </v-col>
                  <v-col sm="6">
                    <InputTextField
                      v-model.number="computedSettings.LOAD_FORECAST.beta"
                      label="Beta"
                      hint="Square root coefficient for peak load calculation"
                      type="number"
                      step="0.01"
                      @change="$emit('change')"
                    ></InputTextField>
                  </v-col>
                </v-row>
              </v-card>
            </v-card-text>
          </v-card>
        </v-card>
      </v-tab-item>
    </v-tabs-items>
  </v-container>
</template>

<script>
import calculationsMixin from "@/mixins/calculationsMixin";
import { schemas } from "data-dictionary";
import { DateTime, Duration } from "luxon";
import inputSelectionMixin from "../mixins/inputSelectionMixin";

export default {
  name: "SystemSettings",
  components: {
    InputPrice: () => import("@/components/InputPrice.vue"),
    InputToggleParam: () => import("@/components/InputToggleParam.vue"),
    InputDateArray: () => import("@/components/InputDateArray.vue"),
    InputDateTime: () => import("@/components/InputDateTime.vue"),
    InputGeneric: () => import("@/components/inputs/InputGeneric.vue"),
    InputTextField: () => import("@/components/inputs/InputTextField.vue"),
    InputProfile: () => import("@/components/InputProfile.vue"),
    InputYearly: () => import("@/components/InputYearly.vue"),
  },
  mixins: [calculationsMixin, inputSelectionMixin],
  props: {
    input: { type: Object, required: true },
    settings: { type: Object, required: true },
    num_years: { type: Number, required: true },
    errors: {
      type: Object,
      required: false,
    },
  },
  methods: {},
  data() {
    return {
      tab: "General",
      powerFlowAlgorithmItems: [
        { text: "Newton-Raphson", value: "nr" },
        { text: "Newton-Raphson with Iwamoto multiplier", value: "iwamoto_nr" },
        { text: "Backward/Forward sweep", value: "bfsw" },
        { text: "Gauss-Seidel", value: "gs" },
        { text: "Fast-Decoupled (bx)", value: "fdbx" },
        { text: "Fast-Decoupled (xb)", value: "fdxb" },
      ],
      powerFlowTransformerModelItems: [
        { text: "T-Model", value: "t" },
        { text: "PI-Model", value: "pi" },
      ],
      tStabilitySources: [
        { text: "Stacked Services Emulator", value: "SSE" },
        // { text: "Nodal Production Cost", value: "NPC" },
        { text: "Case table", value: "CASE_TABLE" },
      ],
      tStabilityIncludeSimulations: [
        { text: "Yes", value: "ALWAYS" },
        // { text: "Only when unstable", value: "ONLY_UNSTABLE" }, // TODO
        { text: "No", value: "NEVER" },
      ],
      isValidEndDate: true,
      isValidStartDate: true,
      disableTopology: true,
      disableFailureClearingTime: true,
      disableReturnAllCurrents: true,
      inputMargin: 4,
      schema: {
        ...schemas.find((v) => v.$id === "settings.schema.json").properties,
      },
      sseSchema: schemas.find((v) => v.$id === "settings.schema.json").properties
        .STACKED_SERVICES_EMULATOR.properties,
      npcTimelineOption: "dates",
      sseTimelineOption: "dates",
      calculationsSettings: [
        "ALTERNATIVE_ANALYSIS",
        "STACKED_SERVICES_EMULATOR",
        "NODAL_PRODUCTION_COST",
        "SHORT_CIRCUIT",
        "POWER_FLOW",
        "ELCC",
        "PRODUCTION_COST",
      ],
    };
  },
  mounted() {},
  computed: {
    computedInput: {
      get() {
        return this.input;
      },
      set(val) {
        this.$emit("update:input", val);
        this.$emit("change");
      },
    },
    computedSettings: {
      get() {
        return this.settings;
      },
      set(val) {
        this.$emit("update:settings", val);
        this.$emit("change");
      },
    },
    systemDateConstraints() {
      return {
        min: `${this.computedSettings.year_start}-01-01`,
        max: `${this.computedSettings.year_end}-12-31`,
      };
    },
    transientTimeParams() {
      const {
        start_datetime: start,
        end_datetime: end,
        simulate_every: interval,
      } = this.computedSettings.TRANSIENT_STABILITY;
      if (start && end && interval) {
        const startDate = DateTime.fromISO(start);
        const endDate = DateTime.fromISO(end);
        const duration = endDate.diff(startDate, "minutes").minutes;
        const simulations = Math.floor(duration / interval);
        return `${simulations} simulations will run`;
      }
      return "Fill parameters above";
    },
    topology() {
      return [
        this.computedSettings.SHORT_CIRCUIT.aperiodic_SC_current,
        this.computedSettings.SHORT_CIRCUIT.equivalent_thermical_SC_current,
      ];
    },
    contingencyTimeOptions() {
      const {
        start_datetime: start,
        end_datetime: end,
        time_interval: interval,
      } = this.computedSettings.POWER_FLOW;
      if (!start || !end || !interval) {
        return ["fill time parameters above"];
      }
      const startDate = DateTime.fromISO(start, { zone: "utc" });
      const endDate = DateTime.fromISO(end, { zone: "utc" });
      const duration = Duration.fromObject({ minutes: interval });

      const opts = [];
      for (let curDate = startDate; curDate < endDate; curDate = curDate.plus(duration)) {
        opts.push({
          text: curDate.toFormat("HH:mm"),
          value: curDate.toISO(),
        });
      }
      return opts;
    },
    failureClearingTime() {
      return this.computedSettings.SHORT_CIRCUIT.equivalent_thermical_SC_current;
    },
    returnAllCurrents() {
      return this.computedSettings.SHORT_CIRCUIT.branch_results;
    },
    nodeIndex() {
      return this.computedInput.nodes
        ? this.computedInput.nodes.map((el) => ({
            name: el.name,
            value: el.id,
          }))
        : [];
    },
    elccSchema() {
      return {
        ...this.schema.ELCC.properties,
        ...this.schema.ELCC.allOf
          .map((v) => v.then.properties)
          .reduce((obj, item) => ({ ...obj, ...item }), {}),
      };
    },
    elccMethodology() {
      return this.settings.ELCC.methodology;
    },
    sequencingIsActive() {
      return (
        this.settings.STACKED_SERVICES_EMULATOR.sequencing &&
        this.settings.STACKED_SERVICES_EMULATOR.sequencing.active
      );
    },
    startYear() {
      return this.settings.year_start;
    },
    endYear() {
      return this.settings.year_end;
    },
    yearItems() {
      return Array(this.num_years)
        .fill(0)
        .map((_v, i) => this.startYear + i);
    },
    historicDataPoints() {
      let numPoints = 1;
      if (this.settings.LOAD_FORECAST && this.settings.LOAD_FORECAST.year_present) {
        numPoints = this.settings.LOAD_FORECAST.year_present - this.settings.year_start;
      }
      if (this.settings.LOAD_FORECAST.granularity === "monthly") {
        numPoints *= 12;
      }
      if (numPoints < 1) {
        numPoints = 1;
      } else if (numPoints > 500) {
        numPoints = 500;
      }
      return numPoints;
    },
    forecastDataPoints() {
      let numPoints = 1;
      if (this.settings.LOAD_FORECAST && this.settings.LOAD_FORECAST.year_present) {
        numPoints = this.settings.year_end - this.settings.LOAD_FORECAST.year_present + 1;
      }
      if (this.settings.LOAD_FORECAST.granularity === "monthly") {
        numPoints *= 12;
      }
      if (numPoints < 1) {
        numPoints = 1;
      } else if (numPoints > 500) {
        numPoints = 500;
      }
      return numPoints;
    },
  },
  watch: {
    returnAllCurrents(val) {
      if (val) {
        this.disableReturnAllCurrents = false;
      } else {
        this.disableReturnAllCurrents = true;

        this.computedSettings.SHORT_CIRCUIT.return_all_currents =
          this.schema.SHORT_CIRCUIT.properties.return_all_currents.default;
      }
    },
    failureClearingTime(val) {
      if (val) {
        this.disableFailureClearingTime = false;
      } else {
        this.disableFailureClearingTime = true;

        this.computedSettings.SHORT_CIRCUIT.failure_clearing_time =
          this.schema.SHORT_CIRCUIT.properties.failure_clearing_time.default;
      }
    },
    topology(val) {
      if (val[0] && val[1]) {
        this.disableTopology = false;
      } else {
        this.disableTopology = true;

        this.computedSettings.SHORT_CIRCUIT.topology =
          this.schema.SHORT_CIRCUIT.properties.topology.default;
      }
    },
    startYear(val) {
      const settingsCopy = this.settings;
      this.isValidStartDate = !!DateTime.fromISO(`${val}-01-01`).isValid;
      settingsCopy.timeline_settings.start_date = DateTime.fromISO(`${val}-01-01`).toISO({
        includeOffset: false,
        suppressMilliseconds: true,
      });
      this.$emit("update:settings", settingsCopy);
      this.$emit("change");
    },
    endYear(val) {
      const settingsCopy = this.settings;
      this.isValidEndDate = !!DateTime.fromISO(`${val}-12-31T23:00:00`).isValid;
      settingsCopy.timeline_settings.end_date = DateTime.fromISO(`${val}-12-31T23:00:00`).toISO({
        includeOffset: false,
        suppressMilliseconds: true,
      });
      this.$emit("update:settings", settingsCopy);
      this.$emit("change");
    },
    elccMethodology(val) {
      const inputCopy = this.input;
      inputCopy.elcc_peaker_generator.methodology = val;
      this.$emit("update:input", inputCopy);
      this.$emit("change");
    },
    npcTimelineOption(v) {
      if (v === "dates") {
        this.computedSettings.NODAL_PRODUCTION_COST.timelineSettings.years = [];
        this.$emit("change");
      }
    },
    sseTimelineOption(v) {
      if (v === "dates") {
        this.computedSettings.timeline_settings.years = [];
        this.$emit("change");
      }
    },
  },
  created() {
    if (!Array.isArray(this.settings.ALTERNATIVE_ANALYSIS.chosen_days)) {
      const settingsCopy = this.settings;
      settingsCopy.ALTERNATIVE_ANALYSIS.chosen_days = [];
      this.$emit("update:settings", settingsCopy);
      this.$emit("change");
    }
    if (!this.settings.NODAL_PRODUCTION_COST.timelineSettings) {
      const settingsCopy = this.settings;
      const { timeline_settings: oldTl } = this.settings.NODAL_PRODUCTION_COST;
      settingsCopy.NODAL_PRODUCTION_COST.timelineSettings = {
        startDate: oldTl.start_date,
        endDate: oldTl.end_date,
        years: [],
      };
      this.computedSettings = settingsCopy;
    }
    if (
      this.computedSettings.NODAL_PRODUCTION_COST.timelineSettings.years &&
      this.computedSettings.NODAL_PRODUCTION_COST.timelineSettings.years.length
    ) {
      this.npcTimelineOption = "years";
    }
    if (
      this.computedSettings.timeline_settings.years &&
      this.computedSettings.timeline_settings.years.length
    ) {
      this.sseTimelineOption = "years";
    }
  },
};
</script>

<style>
.size-36 {
  height: 36px !important;
  width: 36px !important;
}
</style>
